import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import toast from "react-hot-toast";
import {
  Brand,
  BrandType,
  useCreateBrand,
  useUpdateBrand,
} from "../../api/brand";
import { uploadToStorage } from "../../api/media";
import { CustomLoader } from "../../components/CustomLoader/CustomLoader";
import { CustomDropZoneUpload } from "../../components/CustomDropZoneUpload/CustomDropZoneUpload";
import { Icon } from "../../components/Icon/Icon";
import { colors } from "../../styles/colors";
import useStyles from "./styles";

interface Props {
  open: boolean;
  brandInitial: Brand;
  onClose: () => void;
  onCancel: () => void;
}

export const ModalAddBrand: React.FC<Props> = ({
  open,
  brandInitial,
  onClose,
  onCancel,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isEdit = brandInitial.id !== -1;

  const [name, setName] = useState("");
  const [type, setType] = useState<BrandType>("auto");
  const [uid, setUid] = useState("");
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [uploading, setUploading] = useState(false);

  const createBrand = useCreateBrand();
  const updateBrand = useUpdateBrand();

  useEffect(() => {
    if (open) {
      setName(brandInitial.name || "");
      setType((brandInitial.type as BrandType) || "auto");
      setUid(brandInitial.uid || "");
      setLogoUrl(brandInitial.logo_url || "");
    }
  }, [open, brandInitial]);

  const handleLogoUpload = async (files: File[]) => {
    if (!files.length) return;

    const file = files[0];

    // Reject client-side rather than letting the backend 422 after a round
    // trip: the presign endpoint only accepts these MIME types.
    const ACCEPTED = ["image/png", "image/jpeg", "image/webp"];
    if (!ACCEPTED.includes(file.type)) {
      toast.error(
        t("brand.logoBadType", "Format non supporté — utilisez PNG, JPG ou WebP")
      );
      return;
    }

    setUploading(true);
    try {
      // "public/brand-logos" (not "brand"): brand logos are stored in the DB as
      // permanent URLs, so they must live in the anonymously-readable prefix.
      const { url } = await uploadToStorage(file, {
        directory: "public/brand-logos",
      });
      if (!url) throw new Error("no url");
      setLogoUrl(url);
      toast.success(t("brand.logoUploaded", "Logo téléchargé"));
    } catch (e: any) {
      // Surface the real cause (CORS, 413, 422...) instead of one opaque toast.
      console.error("[brand] logo upload failed:", e);
      toast.error(
        e?.message ||
          t("brand.logoUploadError", "Échec du téléchargement du logo")
      );
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error(t("brand.nameRequired", "Le nom de la marque est requis"));
      return;
    }
    const payload = {
      name: name.trim(),
      type,
      uid: uid.trim() || undefined,
      logo_url: logoUrl || null,
    };

    if (isEdit) {
      updateBrand.mutate(
        { id: brandInitial.id, data: payload },
        {
          onSuccess: () => {
            toast.success(t("brand.updated", "Marque mise à jour"));
            onClose();
          },
        }
      );
    } else {
      createBrand.mutate(payload, {
        onSuccess: () => {
          toast.success(t("brand.created", "Marque créée"));
          onClose();
        },
      });
    }
  };

  const isPending =
    createBrand.isPending || updateBrand.isPending || uploading;

  return (
    <>
      {isPending && <CustomLoader isActive={isPending} />}
      <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <Icon
              name={isEdit ? "pencil" : "plus"}
              width="24px"
              height="24px"
              color={colors.primary}
            />
            <Typography variant="h6" component="span" fontWeight={600}>
              {isEdit
                ? t("brand.editBrand", "Modifier la marque")
                : t("brand.addBrand", "Ajouter une marque")}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t("brand.name", "Nom")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Renault"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t("brand.type", "Type")}</InputLabel>
                <Select
                  value={type}
                  label={t("brand.type", "Type")}
                  onChange={(e) => setType(e.target.value as BrandType)}
                >
                  <MenuItem value="auto">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Icon name="car" width="16px" height="16px" color={colors.greydark} />
                      {t("brand.auto", "Auto")}
                    </Box>
                  </MenuItem>
                  <MenuItem value="moto">{t("brand.moto", "Moto")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("brand.uid", "Code (UID)")}
                value={uid}
                onChange={(e) => setUid(e.target.value)}
                helperText={t(
                  "brand.uidHint",
                  "Optionnel — généré automatiquement si laissé vide"
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                gutterBottom
                color="text.secondary"
              >
                {t("brand.logo", "Logo")}
              </Typography>
              <Box className={classes.imageUploadContainer}>
                <CustomDropZoneUpload
                  width="140px"
                  height="140px"
                  borderRadius="8px"
                  imageUrl={logoUrl}
                  onDrop={handleLogoUpload}
                  iconName="photo"
                  iconWidth="40px"
                  iconHeight="40px"
                />
                {logoUrl && (
                  <IconButton
                    className={classes.removeImageButton}
                    size="small"
                    onClick={() => setLogoUrl("")}
                  >
                    <Icon name="close" width="16px" height="16px" color={colors.error} />
                  </IconButton>
                )}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={1}
              >
                {t("brand.logoHint", "Cliquez ou glissez une image (PNG/JPG)")}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={onCancel}
            disabled={isPending}
            variant="outlined"
            startIcon={<Icon name="close" width="16px" height="16px" />}
          >
            {t("common.cancel", "Annuler")}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isPending || !name.trim()}
            startIcon={
              <Icon name={isEdit ? "check" : "plus"} width="16px" height="16px" />
            }
          >
            {isEdit ? t("common.update", "Mettre à jour") : t("common.create", "Créer")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
